import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check, CalendarClock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { COUNTRY_CODES, detectDial } from "@/lib/countryCodes";

const input =
  "w-full rounded-sm bg-zinc-900/70 border border-zinc-800 px-3.5 py-3 font-mono text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-emerald-500 focus:outline-none transition-colors duration-200";
const label = "text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold";

const Chip = ({ active, children, ...rest }) => (
  <button
    type="button"
    {...rest}
    className={`rounded-sm border px-3 py-2 font-mono text-xs transition-colors duration-200 ${
      active
        ? "border-emerald-500 bg-emerald-500/10 text-emerald-300"
        : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"
    }`}
  >
    {children}
  </button>
);

const PER_STEP = 3;

export const ApplyForm = ({ bare = false }) => {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [calendly, setCalendly] = useState("");
  const [questions, setQuestions] = useState([]);
  const [disciplined, setDisciplined] = useState("");
  const [dial, setDial] = useState("+91");
  const [phone, setPhone] = useState("");
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    api
      .get("/public/config")
      .then(({ data }) => setCalendly(data.calendly_url || ""))
      .catch(() => {});
    api
      .get("/public/apply-questions")
      .then(({ data }) => setQuestions(data.questions || []))
      .catch(() => {});
    detectDial().then(setDial);
  }, []);

  const pages = useMemo(() => {
    const out = [];
    for (let i = 0; i < questions.length; i += PER_STEP) {
      out.push(questions.slice(i, i + PER_STEP));
    }
    return out;
  }, [questions]);

  const totalSteps = 1 + pages.length;
  const digits = phone.replace(/\D/g, "");
  const fullPhone = `${dial}${digits}`;
  const step1Ok = disciplined === "yes" && dial && digits.length >= 6;

  const set = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const pageOk = (page) =>
    page.every((q) => !q.required || String(answers[q.id] || "").trim().length > 0);

  const next = () => {
    if (step === 1) {
      if (disciplined !== "yes") {
        toast.error("Pick Yes to continue");
        return;
      }
      if (digits.length < 6) {
        toast.error("Enter a valid phone number");
        return;
      }
    } else if (!pageOk(pages[step - 2])) {
      toast.error("Please answer the required questions");
      return;
    }
    setStep(step + 1);
  };

  const submit = async () => {
    if (!pageOk(pages[step - 2] || [])) {
      toast.error("Please answer the required questions");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/public/leads", {
        phone_country: dial,
        phone: digits,
        disciplined: true,
        answers,
      });
      const link = data.calendly_url || calendly;
      setCalendly(link);
      let booking = "";
      if (link) {
        const q = [
          // "Phone call" event: the phone lives in the location field, not a custom question.
          `location=${encodeURIComponent(fullPhone)}`,
          answers.name ? `name=${encodeURIComponent(String(answers.name))}` : "",
          answers.email ? `email=${encodeURIComponent(String(answers.email))}` : "",
        ]
          .filter(Boolean)
          .join("&");
        booking = `${link}${link.includes("?") ? "&" : "?"}${q}`;
      }
      setDone({ ...data, booking });
      toast.success("Application received");
      if (window.fbq) window.fbq("track", "Lead", { content_name: "Apply for access" });
      if (booking) {
        // Small delay so the pixel Lead event flushes before we leave the page.
        setTimeout(() => window.location.assign(booking), 400);
      }
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };

  if (done) {
    const first = String(answers.name || "").split(" ")[0];
    return (
      <div
        data-testid="apply-success"
        className="border border-emerald-800/60 bg-emerald-500/[0.06] rounded-sm p-6 sm:p-8 text-center"
      >
        <div className="mx-auto h-11 w-11 rounded-full bg-emerald-500/15 border border-emerald-700 flex items-center justify-center">
          <Check className="h-5 w-5 text-emerald-400" />
        </div>
        <h3 className="mt-4 text-lg font-heading font-semibold tracking-tight">
          You're on the list{first ? `, ${first}` : ""}
        </h3>
        <p className="mt-2 text-sm text-zinc-400 leading-relaxed max-w-md mx-auto">
          {done.booking
            ? "Taking you to the booking page to pick a slot for your 1-1 call. Your details are already filled in."
            : "We review every application by hand. Expect a reply on the number you gave us with a call slot."}
        </p>
        {done.booking && (
          <a
            href={done.booking}
            data-testid="calendly-link"
            className="mt-5 inline-flex items-center gap-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black px-5 py-3 text-xs uppercase tracking-[0.12em] font-bold transition-colors duration-200"
          >
            <CalendarClock className="h-4 w-4" /> Book your 1-1 call
          </a>
        )}
      </div>
    );
  }

  const page = step > 1 ? pages[step - 2] || [] : [];
  const lastStep = step === totalSteps && pages.length > 0;

  return (
    <div
      data-testid="apply-form"
      className={
        bare
          ? "space-y-5"
          : "border border-zinc-800 bg-zinc-950/70 rounded-sm p-5 sm:p-7 space-y-5"
      }
    >
      <div className="flex items-center gap-2" data-testid="apply-progress">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
          <span
            key={s}
            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
              s <= step ? "bg-emerald-500" : "bg-zinc-800"
            }`}
          />
        ))}
        <span className="font-mono text-[10px] text-zinc-600 ml-1">
          {step}/{totalSteps}
        </span>
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <div>
            <span className={label}>Do you want to be a Disciplined Trader?</span>
            <div className="mt-2 flex flex-wrap gap-2">
              <Chip
                active={disciplined === "yes"}
                data-testid="apply-disciplined-yes"
                onClick={() => setDisciplined("yes")}
              >
                Yes
              </Chip>
              <Chip
                active={disciplined === "no"}
                data-testid="apply-disciplined-no"
                onClick={() => setDisciplined("no")}
              >
                No
              </Chip>
            </div>
            {disciplined === "no" && (
              <p
                data-testid="apply-disciplined-nudge"
                className="mt-3 text-sm text-emerald-300 leading-relaxed"
              >
                The best are made, not born. Are you sure?
              </p>
            )}
          </div>

          <div>
            <span className={label}>Phone number</span>
            <div className="mt-1.5 flex gap-2 items-stretch">
              <select
                aria-label="Phone country code"
                data-testid="apply-country-code"
                value={dial}
                onChange={(e) => setDial(e.target.value)}
                className="w-[120px] shrink-0 grow-0 rounded-sm bg-zinc-900/70 border border-zinc-800 px-2 py-3 font-mono text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none transition-colors duration-200"
              >
                {[...new Map(COUNTRY_CODES.map((c) => [c.iso, c])).values()].map((c) => (
                  <option key={c.iso} value={c.dial}>
                    {c.iso} {c.dial}
                  </option>
                ))}
              </select>
              <input
                data-testid="apply-phone"
                className={`${input} flex-1 min-w-0`}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="98765 43210"
                inputMode="tel"
              />
            </div>
          </div>
        </div>
      )}

      {step > 1 && (
        <div className="space-y-4">
          {page.map((q) => (
            <div key={q.id}>
              <span className={label}>{q.label}</span>
              {q.type === "choice" ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {q.options.map((o) => (
                    <Chip
                      key={o}
                      active={answers[q.id] === o}
                      data-testid={`apply-${q.id}-${o}`}
                      onClick={() => set(q.id, o)}
                    >
                      {o}
                    </Chip>
                  ))}
                </div>
              ) : q.type === "textarea" ? (
                <textarea
                  rows={3}
                  data-testid={`apply-${q.id}`}
                  className={`${input} mt-1.5 resize-none`}
                  value={answers[q.id] || ""}
                  onChange={(e) => set(q.id, e.target.value)}
                  placeholder={q.placeholder}
                />
              ) : (
                <input
                  data-testid={`apply-${q.id}`}
                  className={`${input} mt-1.5`}
                  value={answers[q.id] || ""}
                  onChange={(e) => set(q.id, e.target.value)}
                  placeholder={q.placeholder}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 pt-1">
        {step > 1 && (
          <button
            type="button"
            data-testid="apply-back"
            onClick={() => setStep(step - 1)}
            className="px-4 py-3 text-xs uppercase tracking-[0.12em] font-semibold text-zinc-500 hover:text-zinc-200 transition-colors duration-200"
          >
            Back
          </button>
        )}
        <div className="flex-1" />
        {!lastStep ? (
          <button
            type="button"
            onClick={next}
            disabled={(step === 1 && !step1Ok) || pages.length === 0}
            data-testid="apply-next"
            className="inline-flex items-center gap-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-black px-5 py-3 text-xs uppercase tracking-[0.12em] font-bold transition-colors duration-200 active:scale-95"
          >
            Continue <ArrowRight className="h-3.5 w-3.5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="apply-submit"
            className="inline-flex items-center gap-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black px-5 py-3 text-xs uppercase tracking-[0.12em] font-bold transition-colors duration-200 active:scale-95"
          >
            {busy ? "Sending…" : "Apply for access"}
          </button>
        )}
      </div>
      {lastStep && (
        <p
          className="mt-4 text-[11px] leading-relaxed text-zinc-600"
          data-testid="apply-consent"
        >
          By applying you agree to our{" "}
          <a className="text-emerald-500 hover:text-emerald-400" href="/terms" target="_blank" rel="noreferrer">
            terms
          </a>
          ,{" "}
          <a className="text-emerald-500 hover:text-emerald-400" href="/privacy" target="_blank" rel="noreferrer">
            privacy policy
          </a>{" "}
          and{" "}
          <a
            className="text-emerald-500 hover:text-emerald-400"
            href="/risk-disclosure"
            target="_blank"
            rel="noreferrer"
          >
            risk disclosure
          </a>
          , and you consent to us contacting you about your application. Strict RR is software only —
          no signals, no advice, no guaranteed returns. Active market participation carries risk of loss.
        </p>
      )}
    </div>
  );
};
